import { CoppaAudienceComponent } from './data-widgets/coppa-audience.component';
import { ReportTypeKey } from './types/report-type-key';
import { GridHeader } from './models/gridHeader';
import { MainFilterItem } from './models/mainFilterItem';
import { GridHeaderComponent } from './data-widgets/grid-header.component';
import { GridHeaderWithTooltipComponent } from './data-widgets/grid-header-with-tooltip.component';
import { GridHeaderWithPopoverComponent } from './data-widgets/grid-header-with-popover.component';
import { SellerNameComponent } from './data-widgets/seller-name.component';
import { AppNameComponent } from './data-widgets/app-name.component';
import { PublisherNameComponent } from './data-widgets/publisher-name.component';
import { RankDataComponent } from './data-widgets/rank-data.component';
import { RankGradeComponent } from './data-widgets/rank-grade.component';
import { DataOnlyComponent } from './data-widgets/data-only.component';
import { PercentDataComponent } from './data-widgets/percent-data.component';
import { DollarCurrencyDataComponent } from './data-widgets/dollar-currency-data.component';
import { BigNumberDataComponent } from './data-widgets/big-number-data.component';
import { YesnoComponent } from './data-widgets/yesno.component';
import { RatingBarComponent } from './data-widgets/rating-bar.component';
import { DomainNameComponent } from './data-widgets/domain-name.component';
import { SellerCountryComponent } from './data-widgets/seller-country.component';
import { SellerRegionComponent } from './data-widgets/seller-region.component';
import { GridHeaderWithPopoverSearchComponent } from './data-widgets/grid-header-with-popover-search.component';
import { RatingPillComponent } from './data-widgets/rating-pill.component';
import { TOOLTIPS } from 'app/constants/TOOLTIPS';

export interface ReportConfig {
  key: ReportTypeKey;
  title: string;
  heading: string;
  reportId: string;
  mainFilterItems: MainFilterItem[];
  gridHeaders: GridHeader[];
  permissions: string[];
  defaultSort?: string;
  pageTitle?: string;
  pageMeta?: string;
  sortable?: boolean;
  showTitle?: boolean;
  notHideDataIfNA?: boolean;
}

export const globalSellerTemplate =
  '<img class="sicon" src="https://cdn.pixalate.com/sellers/icons/apn/16x16/${id}.jpg"' +
  'onerror="this.style.opacity=0;" alt="${name} Icon"/>&nbsp;<span class="sellerName text-uppercase">${name}</span>';
export const siteDomainTemplate =
  '<img class="sicon" src="http://logo.clearbit.com/${adDomain}"' +
  'onerror="this.style.opacity=0;" alt="${adDomain} Icon"/>&nbsp;<span class="sellerName text-uppercase">${adDomain}</span>';
// http://logo.clearbit.com/pixalate.com

export const CTV_OPTIONS = [
  {
    label:
      '<img class="mr-2" src="/assets/images/roku.png" alt="Roku Icon"/> Roku',
    value: 'roku'
  },
  {
    label:
      '<img class="mr-2" src="/assets/images/firetv.png" alt="Fire TV Icon"/> FireTV',
    value: 'firetv'
  },
  {
    label:
      '<img class="mr-1" src="/assets/images/samsung.png" alt="Samsung Icon"/> Samsung',
    value: 'samsung'
  },
  {
    label:
      '<img class="mr-1" src="/assets/images/tvos.png" alt="tvOS Icon"/> tvOS',
    value: 'tvos'
  }
];

export const CTV_STI_OPTIONS = [
  {
    label:
      '<img class="mr-2" src="/assets/images/roku.png" alt="Roku Icon"/> Roku',
    value: 'Roku'
  },
  {
    label:
      '<img class="mr-2" src="/assets/images/firetv.png" alt="Amazon Icon"/> Amazon',
    value: 'Amazon'
  },
  {
    label:
      '<img class="mr-1" src="/assets/images/samsung.png" alt="Samsung Icon"/> Samsung',
    value: 'Samsung'
  },
  {
    label:
      '<img class="mr-1" src="/assets/images/tvos.png" alt="tvOS Icon"/> tvOS',
    value: 'tvOS'
  }
];

export const REPORT_CONFIG: ReportConfig[] = [
  {
    key: 'seller-trust-index',
    heading: 'Seller Trust Index',
    title: 'Seller Trust Index',
    reportId: 'sellerTrustIndex',
    permissions: ['mrtSellerTrustIndex', 'freemium'],
    mainFilterItems: [
      {
        key: 'platformName',
        label: 'OS',
        selected: CTV_STI_OPTIONS[0],
        groups: ['ctv'],
        hidden: false,
        type: 'toggle-filter',
        options: [...CTV_STI_OPTIONS]
      },
      {
        key: 'countryCode',
        label: 'Region',
        selected: { label: 'US', value: 'US' },
        hidden: false,
        dependencies: [
          {
            key: 'date'
          },
          {
            key: 'platformName'
          }
        ],
        options: [
          { label: 'US', value: 'US' },
          { label: 'NON-US', value: 'NON-US' }
        ]
      },
      {
        key: 'countryCode',
        label: 'Country',
        selected: { label: 'All Countries', value: 'GLOBAL' },
        hidden: false,
        dependencies: [
          {
            group: 'ctv',
            key: 'platformName'
          },
          {
            key: 'date'
          },
          {
            label: 'Region'
          }
        ],
        options: [{ label: 'All Countries', value: 'GLOBAL' }]
      },
      {
        key: 'date',
        label: 'Month',
        selected: null,
        hidden: false,
        dependencies: [
          {
            group: 'app',
            key: 'platformName'
          }
        ],
        options: []
      }
    ],
    notHideDataIfNA: true,
    gridHeaders: [
      {
        label: 'Rank',
        key: 'rank',
        sort: '',
        sortDefault: false,
        sortColumnKey: '',
        hoverKey: '',
        template: null,
        hidden: false,
        widgetComponent: RankDataComponent,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Seller',
        key: 'sellerName',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: SellerNameComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderWithPopoverSearchComponent
      },
      {
        label: 'Final Score',
        key: 'finalScore',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Market Share Score',
        key: 'marketShareScore',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderWithTooltipComponent,
        tooltipText: {
          app: TOOLTIPS.marketShareScore,
          ctv: TOOLTIPS.marketShareScore,
          domain: TOOLTIPS.marketShareScore
        }
      },
      {
        label: 'SPO Score',
        key: 'spoOptimizationScore',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderWithTooltipComponent,
        tooltipText: {
          app: TOOLTIPS.spoOptimizationScore,
          ctv: TOOLTIPS.spoOptimizationScore,
          domain: TOOLTIPS.spoOptimizationScore
        }
      },
      {
        label: 'SCO Verification Score',
        key: 'scoVerificationScore',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderWithTooltipComponent,
        tooltipText: {
          app: TOOLTIPS.scoVerificationScore,
          ctv: TOOLTIPS.scoVerificationScore,
          domain: TOOLTIPS.scoVerificationScore
        }
      },
      {
        label: 'Bundle Integrity',
        key: 'bundleIntegrityScore',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderWithTooltipComponent,
        tooltipText: {
          app: null,
          ctv: `Represents the amount of traffic from the seller that uses standardized app store IDs in the bid stream.`,
          domain: null
        }
      },
      {
        label: 'Reach Score',
        key: 'reachScore',
        groups: ['app', 'domain'],
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'GIVT Score',
        key: 'givtScore',
        groups: ['app', 'domain'],
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderWithTooltipComponent,
        tooltipText: {
          app: TOOLTIPS.givtScore,
          ctv: null,
          domain: TOOLTIPS.givtScore
        }
      },
      {
        label: 'SIVT Score',
        key: 'sivtScore',
        groups: ['app', 'domain'],
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderWithTooltipComponent,
        tooltipText: {
          app: TOOLTIPS.sivtScore,
          ctv: null,
          domain: TOOLTIPS.sivtScore
        }
      },
      {
        label: 'Transparency Score',
        key: 'transparencyScore',
        groups: ['app'],
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Location Score',
        key: 'locationScore',
        groups: ['app'],
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'IVT Score',
        key: 'ivtScore',
        sort: '',
        groups: ['ctv'],
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderWithTooltipComponent,
        tooltipText: {
          app: null,
          ctv: `Reflects the levels of Invalid Traffic (IVT) on the impressions associated with the seller.`,
          domain: null
        }
      },
      {
        label: 'Household Score',
        key: 'householdScore',
        sort: '',
        groups: ['ctv'],
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Channel Share Score',
        key: 'channelShareScore',
        sort: '',
        groups: ['ctv'],
        sortDefault: false,
        sortColumnKey: '',
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'SSAI Score',
        key: 'ssaiScore',
        sort: '',
        groups: ['ctv'],
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Spoofing Score',
        key: 'spoofingScore',
        sort: '',
        groups: ['ctv'],
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Sideloaded Score',
        key: 'sideloadedScore',
        sort: '',
        groups: ['ctv'],
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent,
        showIfReq: {
          isFilter: {
            platform: 'Amazon'
          }
        }
      },
      {
        label: 'Ads.txt Score',
        key: 'adsTxtScore',
        sort: '',
        groups: ['domain'],
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Masking Score',
        key: 'maskingScore',
        sort: '',
        groups: ['domain'],
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'MFA Risk Score',
        key: 'mfaRiskScore',
        sort: '',
        groups: ['domain'],
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RatingPillComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderWithTooltipComponent,
        tooltipText: {
          app: TOOLTIPS.mfaRiskScore,
          ctv: null,
          domain: TOOLTIPS.mfaRiskScore
        }
      },
      {
        label: 'Viewability Score',
        key: 'viewabilityScore',
        sort: '',
        groups: ['domain'],
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Website Count',
        key: 'websiteCount',
        sort: '',
        groups: ['domain'],
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: BigNumberDataComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      }
    ],
    pageTitle:
      'Seller Trust Index - Quality Seller Rankings for the Programmatic Supply Chain',
    pageMeta:
      'Seller Trust Index, a Pixalate Rankings index, is the rating standard for measuring seller quality across {type}',
    sortable: true
  },
  {
    key: 'seller-market-share',
    heading: 'Market Share',
    title: 'Market Share',
    reportId: 'sellerMarketShare',
    permissions: ['mrtSellerTrustIndex', 'freemium'],
    mainFilterItems: [
      {
        key: 'platformName',
        label: 'OS',
        selected: {
          label:
            '<img class="mr-2" src="/assets/images/google-play.svg" alt="Google Play Icon"/> Google Play',
          value: 'android'
        },
        groups: ['app'],
        hidden: false,
        type: 'toggle-filter',
        options: [
          {
            label:
              '<img class="mr-2" src="/assets/images/google-play.svg" alt="Google Play Icon"/> Google Play',
            value: 'android'
          },
          {
            label:
              '<img class="mr-2" src="/assets/images/apple.svg" alt="App Store Icon"/> App Store',
            value: 'ios'
          }
        ]
      },
      {
        key: 'platformName',
        label: 'OS',
        selected: CTV_OPTIONS[0],
        groups: ['ctv'],
        hidden: false,
        type: 'toggle-filter',
        options: [...CTV_OPTIONS]
      },
      {
        key: 'category',
        label: 'Category',
        selected: { label: 'All Categories', value: 'GLOBAL' },
        groups: ['app', 'ctv'],
        hidden: false,
        dependencies: [
          {
            key: 'countryCode'
          },
          {
            key: 'date'
          },
          {
            key: 'platformName'
          }
        ],
        options: [{ label: 'All Categories', value: 'GLOBAL' }]
      },
      {
        key: 'countryCode',
        label: 'Region',
        selected: { label: 'North America', value: 'NA' },
        hidden: false,
        dependencies: [
          {
            key: 'platformName'
          },
          {
            key: 'date'
          }
        ],
        options: [{ label: 'North America', value: 'NA' }]
      },
      {
        key: 'countryCode',
        label: 'Country',
        selected: { label: 'All Countries', value: 'GLOBAL' },
        hidden: false,
        dependencies: [
          {
            key: 'date'
          },
          {
            key: 'platformName'
          },
          {
            label: 'Region'
          }
        ],
        options: [{ label: 'All Countries', value: 'GLOBAL' }]
      },
      {
        key: 'date',
        label: 'Month',
        selected: null,
        hidden: false,
        dependencies: [
          {
            group: 'app',
            key: 'platformName'
          }
        ],
        options: []
      }
    ],
    gridHeaders: [
      {
        label: 'Rank',
        key: 'impressionRank',
        sort: '',
        sortDefault: false,
        sortColumnKey: '',
        hoverKey: '',
        template: null,
        widgetComponent: RankDataComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Seller',
        key: 'sellerName',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        groups: ['app', 'ctv'],
        hoverKey: '',
        template: null,
        widgetComponent: SellerNameComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Seller',
        key: 'sellerName',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        groups: ['domain'],
        hoverKey: '',
        template: null,
        widgetComponent: SellerNameComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Estimated Market Reach',
        key: 'impressionSov',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        format: 'percent',
        template: null,
        widgetComponent: PercentDataComponent,
        hidden: false,
        lockIfFreemium: true,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Spacer',
        key: 'spacer',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: null,
        hidden: false,
        format: '50%',
        headerWidgetComponent: GridHeaderComponent
      }
    ],
    pageTitle: 'Market Share for SSP’s, a Pixalate Rankings index: for {type}',
    pageMeta:
      'Seller Market Share measures estimated market reach for {type} in the {platformName}.'
  },
  {
    key: 'seller-childrens-privacy',
    heading: "Children's Privacy Index",
    title: 'Market Share',
    reportId: 'sellerCoppaIndex',
    permissions: ['mrtSellerTrustIndex', 'freemium'],
    mainFilterItems: [
      {
        key: 'date',
        label: 'Month',
        selected: null,
        hidden: false,
        options: []
      }
    ],
    gridHeaders: [
      {
        label: 'Seller',
        key: 'sellerName',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        groups: ['app'],
        hoverKey: '',
        template: null,
        widgetComponent: SellerNameComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Child Audience Risk',
        key: 'childDirectedGrade',
        sort: 'sort-asc',
        sortDefault: false,
        sortColumnKey: 'rank',
        addSortColumnKeys: ['sellerSort'],
        hoverKey: '',
        template: null,
        widgetComponent: RatingBarComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderWithTooltipComponent,
        tooltipText: {
          app: 'Child Audience Risk is a comparative measure of the percentage of a Seller’s ad impressions within likely child-directed (including mixed audience) apps as compared to their peers. It is sourced from 3rd-party data, and is NOT a factor in any other Pixalate Seller Trust Index scores',
          ctv: null,
          domain: null
        }
      },
      {
        label: 'Spacer',
        key: 'spacer',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        groups: ['app'],
        hoverKey: '',
        template: null,
        widgetComponent: null,
        hidden: false,
        format: '50%',
        headerWidgetComponent: GridHeaderComponent
      }
    ],
    pageTitle:
      "Children's Privacy for SSP’s, a Pixalate Rankings index: for {type}",
    pageMeta:
      "Children's Privacy measures estimated ercentage of a Seller’s ad impressions within likely child-directed (including mixed audience) {type} as compared to their peers.",
    sortable: true
  },
  {
    key: 'publisher-trust-index',
    heading: 'Publisher Trust Index',
    title: 'Publisher Trust Index',
    reportId: 'publisherTrustIndex',
    permissions: ['mrtSellerTrustIndex', 'freemium'],
    mainFilterItems: [
      {
        key: 'platformName',
        label: 'OS',
        selected: {
          label:
            '<img class="mr-2" src="/assets/images/google-play.svg" alt="Google Play Icon"/> Android',
          value: 'android'
        },
        groups: ['app'],
        hidden: false,
        type: 'toggle-filter',
        options: [
          {
            label:
              '<img class="mr-2" src="/assets/images/google-play.svg" alt="Google Play Icon"/> Google Play',
            value: 'android'
          },
          {
            label:
              '<img class="mr-2" src="/assets/images/apple.svg" alt="App Store Icon"/> App Store',
            value: 'ios'
          }
        ]
      },
      {
        key: 'platformName',
        label: 'OS',
        selected: CTV_OPTIONS[0],
        groups: ['ctv'],
        hidden: false,
        type: 'toggle-filter',
        options: [...CTV_OPTIONS]
      },
      {
        key: 'category',
        label: 'Category',
        selected: { label: 'All Categories', value: 'GLOBAL' },
        hidden: false,
        dependencies: [
          {
            label: 'Region'
          },
          {
            key: 'date'
          },
          {
            key: 'platformName'
          }
        ],
        options: [{ label: 'All Categories', value: 'GLOBAL' }]
      },
      {
        key: 'countryCode',
        label: 'Region',
        selected: { label: 'North America', value: 'NA' },
        hidden: false,
        dependencies: [
          {
            key: 'platformName'
          },
          {
            key: 'date'
          }
        ],
        options: [{ label: 'North America', value: 'NA' }]
      },
      {
        key: 'countryCode',
        label: 'Country',
        selected: { label: 'All Countries', value: 'GLOBAL' },
        hidden: false,
        dependencies: [
          {
            key: 'platformName'
          },
          {
            key: 'date'
          },
          {
            label: 'Region'
          }
        ],
        options: [{ label: 'All Countries', value: 'GLOBAL' }]
      },
      {
        key: 'date',
        label: 'Month',
        selected: null,
        hidden: false,
        dependencies: [
          {
            group: 'app',
            key: 'platformName'
          }
        ],
        options: []
      }
    ],
    gridHeaders: [
      {
        label: 'Rank',
        key: 'rank',
        sort: 'sort-asc',
        sortDefault: false,
        sortColumnKey: 'rank',
        hoverKey: '',
        template: null, // <div class="rank-change" data-rank-value="${rankChange}">${rankChange} <i class="fa fa-arrow-up"></i><i class="fa fa-arrow-down"></i></div>',
        widgetComponent: RankDataComponent,
        hidden: false,
        tooltipText: {
          app: null,
          ctv: null,
          domain: null
        },
        headerWidgetComponent: GridHeaderWithTooltipComponent
      },
      {
        label: 'App Name',
        key: 'appTitle',
        groups: ['app', 'ctv'],
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: AppNameComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Domain',
        key: 'adDomain',
        groups: ['domain'],
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: DomainNameComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Final<br/>Score',
        key: 'finalScore',
        sort: '',
        sortDefault: false,
        sortColumnKey: 'finalScore',
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        tooltipText: {
          app: 'This score corresponds to the average of all the individual scores, excluding the COPPA Audience Assessment.',
          ctv: null,
          domain: null
        },
        headerWidgetComponent: GridHeaderWithTooltipComponent
      },
      {
        label: 'Popularity<br/>Score',
        key: 'popularityScore',
        sort: '',
        sortDefault: false,
        sortColumnKey: 'popularityScore',
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        tooltipText: {
          app: 'Programmatic reach score is calculated in terms of reach and inventory availability to illustrate potential reach as compared to the their competitors.',
          ctv: null,
          domain: null
        },
        headerWidgetComponent: GridHeaderWithTooltipComponent
      },
      {
        label: 'IVT<br/>Score',
        key: 'ivtScore',
        sort: '',
        sortDefault: false,
        sortColumnKey: 'ivtScore',
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        tooltipText: {
          app: 'IVT Score corresponds to the percentage of IVT of the publisher compared to their peers in a given category & geography.',
          ctv: null,
          domain: null
        },
        headerWidgetComponent: GridHeaderWithTooltipComponent
      },
      {
        label: 'Brand Safety<br/>Score',
        key: 'brandSafetyScore',
        sort: '',
        groups: ['app', 'domain'],
        sortDefault: false,
        sortColumnKey: 'brandSafetyScore',
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        tooltipText: {
          app: 'This score captures our assessment of brand unsafe keywords identified in the app content description and or brand unsafe advisories declared by a given app in their app store page.',
          ctv: null,
          domain: null
        },
        headerWidgetComponent: GridHeaderWithTooltipComponent
      },
      {
        label: 'Ads.txt<br/>Score',
        key: 'adsTxtScore',
        sort: '',
        groups: ['app', 'domain'],
        sortDefault: false,
        sortColumnKey: 'adsTxtScore',
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        tooltipText: {
          app: 'This score corresponds to the detected presence of ads.txt for a given publisher, as well as the presence of a “balanced” mix of sellers and resellers compared to its competitors.',
          ctv: null,
          domain: null
        },
        headerWidgetComponent: GridHeaderWithTooltipComponent
      },
      {
        label: 'MFA Score',
        key: 'mfaScore',
        sort: '',
        groups: ['domain'],
        sortDefault: false,
        sortColumnKey: 'mfaScore',
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        tooltipText: {
          app: null,
          ctv: null,
          domain: null
        },
        headerWidgetComponent: GridHeaderWithTooltipComponent
      },
      {
        label: 'Ad Density<br/>Score',
        key: 'adDensityScore',
        sort: '',
        groups: ['domain'],
        sortDefault: false,
        sortColumnKey: 'adDensityScore',
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        tooltipText: {
          app: null,
          ctv: null,
          domain: null
        },
        headerWidgetComponent: GridHeaderWithTooltipComponent
      },
      {
        label: 'Viewability<br/>Score',
        key: 'viewabilityScore',
        sort: '',
        groups: ['app', 'domain'],
        sortDefault: false,
        sortColumnKey: 'viewabilityScore',
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        tooltipText: {
          app: 'This score corresponds to the performance of the app in terms of its viewability.',
          ctv: null,
          domain: null
        },
        headerWidgetComponent: GridHeaderWithTooltipComponent
      },
      {
        label: 'Permission<br/>Score',
        key: 'permissionsScore',
        sort: '',
        groups: ['app'],
        sortDefault: false,
        sortColumnKey: 'permissionsScore',
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        tooltipText: {
          app: 'This score corresponds to the increase in the likelihood of the presence of IVT when specific permissions are requested by the app that are not common for its category.',
          ctv: null,
          domain: null
        },
        tooltipPos: 'left',
        headerWidgetComponent: GridHeaderWithTooltipComponent,
        showIfReq: {
          isFilter: {
            platform: 'android'
          }
        }
      },
      {
        label: 'Ad Density<br/>Score',
        key: 'adDensityScore',
        sort: '',
        groups: ['ctv'],
        sortDefault: false,
        sortColumnKey: 'adDensityScore',
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        tooltipText: {
          app: null,
          ctv: null,
          domain: null
        },
        headerWidgetComponent: GridHeaderWithTooltipComponent
      },
      {
        label: 'Engagement<br/>Score',
        key: 'userEngagementScore',
        sort: '',
        groups: ['ctv'],
        sortDefault: false,
        sortColumnKey: 'userEngagementScore',
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        tooltipText: {
          app: null,
          ctv: null,
          domain: null
        },
        headerWidgetComponent: GridHeaderWithTooltipComponent
      },
      {
        label: 'COPPA Audience<br/>Assessment',
        key: 'coppaAudience',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: CoppaAudienceComponent,
        hidden: false,
        groups: ['app', 'ctv'],
        tooltipText: {
          app: "Pixalate's assessment of the likely target audience of the app, according to Pixalate's COPPA methodology. This assessment is not factored into the Final Score.<br/><a target='_blank' href=\"https://www.pixalate.com/coppa-compliance-tools-methodology\">See methodology <i class='fa fa-external-link'></i></a>",
          ctv: "Pixalate's assessment of the likely target audience of the app, according to Pixalate's COPPA methodology. This assessment is not factored into the Final Score.<br/><a target='_blank' href=\"https://www.pixalate.com/coppa-compliance-tools-methodology\">See methodology <i class='fa fa-external-link'></i></a>",
          domain: null
        },
        headerWidgetComponent: GridHeaderWithPopoverComponent
      }
    ],
    pageTitle:
      'Publisher Trust Index - Quality Publisher Rankings for the Programmatic Supply Chain',
    pageMeta:
      'Publisher Trust Index, a Pixalate Rankings index, introduces a new rating standard for measuring publisher quality across {type} in the {platformName}.',
    sortable: true
  },
  {
    key: 'publisher-grossing',
    heading: 'Top Grossing',
    title: 'Top Grossing',
    reportId: 'publisherGrossing',
    permissions: ['mrtSellerTrustIndex', 'freemium'],
    mainFilterItems: [
      {
        key: 'platformName',
        label: 'OS',
        selected: {
          label:
            '<img class="mr-2" src="/assets/images/google-play.svg" alt="Google Play Icon"/> Google Play',
          value: 'android'
        },
        groups: ['app'],
        hidden: false,
        type: 'toggle-filter',
        options: [
          {
            label:
              '<img class="mr-2" src="/assets/images/google-play.svg" alt="Google Play Icon"/> Google Play',
            value: 'android'
          },
          {
            label:
              '<img class="mr-2" src="/assets/images/apple.svg" alt="App Store Icon"/> App Store',
            value: 'ios'
          }
        ]
      },
      {
        key: 'platformName',
        label: 'OS',
        selected: CTV_OPTIONS[0],
        groups: ['ctv'],
        hidden: false,
        type: 'toggle-filter',
        options: [...CTV_OPTIONS]
      },
      {
        key: 'category',
        label: 'Category',
        selected: { label: 'All Categories', value: 'GLOBAL' },
        hidden: false,
        dependencies: [
          {
            key: 'countryCode'
          },
          {
            key: 'date'
          },
          {
            key: 'platformName'
          }
        ],
        options: [{ label: 'All Categories', value: 'GLOBAL' }]
      },
      {
        key: 'countryCode',
        label: 'Region',
        selected: { label: 'North America', value: 'NA' },
        hidden: false,
        dependencies: [
          {
            key: 'platformName'
          },
          {
            key: 'date'
          }
        ],
        options: [{ label: 'North America', value: 'NA' }]
      },
      {
        key: 'countryCode',
        label: 'Country',
        selected: { label: 'All Countries', value: 'GLOBAL' },
        hidden: false,
        dependencies: [
          {
            key: 'date'
          },
          {
            key: 'platformName'
          },
          {
            label: 'Region'
          }
        ],
        options: [{ label: 'All Countries', value: 'GLOBAL' }]
      },
      {
        key: 'date',
        label: 'Month',
        selected: null,
        hidden: false,
        dependencies: [
          {
            group: 'app',
            key: 'platformName'
          }
        ],
        options: []
      }
    ],
    gridHeaders: [
      {
        label: 'Rank',
        key: 'adSpentRank',
        sort: '',
        sortDefault: true,
        sortColumnKey: '',
        hoverKey: '',
        template: null, // <div class="rank-change" data-rank-value="${adSpentRankChange}">${adSpentRankChange} <i class="fa fa-arrow-up"></i><i class="fa fa-arrow-down"></i></div>',
        widgetComponent: RankDataComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'App Name',
        key: 'appTitle',
        groups: ['app', 'ctv'],
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: AppNameComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Publisher Name',
        key: 'publisherName',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        hidden: false,
        widgetComponent: PublisherNameComponent,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Estimated\u0020Programmatic\u0020Spend',
        key: 'adSpent',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        lockIfFreemium: true,
        format: 'big-number-no-decimal',
        template: null,
        widgetComponent: DollarCurrencyDataComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      }
    ],
    pageTitle: 'Top Grossing Publishers, a Pixalate Rankings index: for {type}',
    pageMeta:
      'Publisher Top-Grossing measures estimated programmatic spend for {type} in the {platformName}'
  },
  {
    key: 'publisher-reach',
    heading: 'Reach',
    title: 'Reach',
    reportId: 'publisherReach',
    permissions: ['mrtSellerTrustIndex', 'freemium'],
    mainFilterItems: [
      {
        key: 'platformName',
        label: 'OS',
        selected: {
          label:
            '<img class="mr-2" src="/assets/images/google-play.svg" alt="Google Play Icon"/> Google Play',
          value: 'android'
        },
        groups: ['app'],
        hidden: false,
        type: 'toggle-filter',
        options: [
          {
            label:
              '<img class="mr-2" src="/assets/images/google-play.svg" alt="Google Play Icon"/> Google Play',
            value: 'android'
          },
          {
            label:
              '<img class="mr-2" src="/assets/images/apple.svg" alt="App Store Icon"/> App Store',
            value: 'ios'
          }
        ]
      },
      {
        key: 'platformName',
        label: 'OS',
        selected: CTV_OPTIONS[0],
        groups: ['ctv'],
        hidden: false,
        type: 'toggle-filter',
        options: [...CTV_OPTIONS]
      },
      {
        key: 'category',
        label: 'Category',
        selected: { label: 'All Categories', value: 'GLOBAL' },
        hidden: false,
        dependencies: [
          {
            key: 'countryCode'
          },
          {
            key: 'date'
          },
          {
            key: 'platformName'
          }
        ],
        options: [{ label: 'All Categories', value: 'GLOBAL' }]
      },
      {
        key: 'countryCode',
        label: 'Region',
        selected: { label: 'North America', value: 'NA' },
        hidden: false,
        dependencies: [
          {
            key: 'date'
          },
          {
            key: 'platformName'
          }
        ],
        options: [{ label: 'North America', value: 'NA' }]
      },
      {
        key: 'countryCode',
        label: 'Country',
        selected: { label: 'All Countries', value: 'GLOBAL' },
        hidden: false,
        dependencies: [
          {
            key: 'date'
          },
          {
            key: 'platformName'
          },
          {
            label: 'Region'
          }
        ],
        options: [{ label: 'All Countries', value: 'GLOBAL' }]
      },
      {
        key: 'date',
        label: 'Month',
        selected: null,
        hidden: false,
        dependencies: [
          {
            group: 'app',
            key: 'platformName'
          }
        ],
        options: []
      }
    ],
    gridHeaders: [
      {
        label: 'Rank',
        key: 'mauRank',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null, // <div class="rank-change" data-rank-value="${mauRankChange}">${mauRankChange} <i class="fa fa-arrow-up"></i><i class="fa fa-arrow-down"></i></div>',
        widgetComponent: RankDataComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'App Name',
        key: 'appTitle',
        groups: ['app', 'ctv'],
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        widgetComponent: AppNameComponent,
        template: null,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Publisher Name',
        key: 'publisherName',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        hidden: false,
        widgetComponent: PublisherNameComponent,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Estimated Reach',
        key: 'mau',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        lockIfFreemium: true,
        format: '0.00a',
        template: null,
        widgetComponent: BigNumberDataComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      }
    ],
    pageTitle:
      'Estimated Reach of Publishers, a Pixalate Rankings index:  for {type}',
    pageMeta:
      'Publisher Estimated Reach measures estimated reach for {type} in the {platformName}'
  },
  {
    key: 'publisher-coppa',
    heading: 'Child-Directed Apps',
    title: 'Popular Apps Registered in {countryCode}',
    showTitle: true,
    reportId: 'coppaIndex',
    notHideDataIfNA: true,
    permissions: ['mrtSellerTrustIndex', 'freemium'],
    mainFilterItems: [
      {
        key: 'platformName',
        label: 'OS',
        selected: {
          label:
            '<img class="mr-2" src="/assets/images/google-play.svg" alt="Google Play Icon"/> Google Play',
          value: 'android'
        },
        groups: ['app'],
        hidden: false,
        type: 'toggle-filter',
        options: [
          {
            label:
              '<img class="mr-2" src="/assets/images/google-play.svg" alt="Google Play Icon"/> Google Play',
            value: 'android'
          },
          {
            label:
              '<img class="mr-2" src="/assets/images/apple.svg" alt="App Store Icon"/> App Store',
            value: 'ios'
          }
        ]
      },
      {
        key: 'platformName',
        label: 'OS',
        selected: CTV_OPTIONS[0],
        groups: ['ctv'],
        hidden: false,
        type: 'toggle-filter',
        options: [...CTV_OPTIONS]
      },
      {
        key: 'countryCode',
        apiKey: 'country',
        label: 'Country',
        selected: { label: 'All Countries', value: 'GLOBAL' },
        hidden: false,
        dependencies: [
          {
            key: 'date'
          },
          {
            key: 'platformName'
          }
        ],
        options: [{ label: 'All Countries', value: 'GLOBAL' }]
      },
      {
        key: 'date',
        label: 'Month',
        selected: null,
        hidden: false,
        dependencies: [
          {
            key: 'platformName'
          }
        ],
        options: []
      }
    ],
    gridHeaders: [
      {
        label: 'Rank',
        key: 'rank',
        sort: '',
        sortDefault: true,
        sortColumnKey: '',
        hoverKey: '',
        template: null,
        widgetComponent: RankDataComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'App Name',
        key: 'appTitle',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        widgetComponent: AppNameComponent,
        template: null,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Estimated Downloads',
        key: 'downloads',
        showIfReq: {
          isFilter: {
            platform: 'android'
          }
        },
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        format: '0a,text-uppercase',
        template: null,
        widgetComponent: BigNumberDataComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Assessment Type',
        key: 'manuallyReviewed',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        hidden: false,
        format: 'manual-review',
        widgetComponent: YesnoComponent,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Privacy Policy Detected',
        key: 'privacyPolicyDetected',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        hidden: false,
        widgetComponent: YesnoComponent,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Sensitive Permissions Detected',
        key: 'sensitivePermissions',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        hidden: false,
        format: 'rev',
        widgetComponent: YesnoComponent,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Transmits Location',
        key: 'transmitsLocation',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        hidden: false,
        format: 'rev',
        widgetComponent: YesnoComponent,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Transmits Residential IP',
        key: 'transmitsResidentialIP',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        hidden: false,
        format: 'rev',
        widgetComponent: YesnoComponent,
        headerWidgetComponent: GridHeaderComponent
      }
    ],
    pageTitle: 'Top 100 Likely Child-directed Apps',
    pageMeta:
      'Top 100 Likely Child-directed Apps for {type} in the {platformName}'
  },
  {
    key: 'device-reach',
    heading: 'Reach',
    title: 'Reach',
    reportId: 'iotDeviceReach',
    permissions: ['mrtSellerTrustIndex', 'freemium'],
    mainFilterItems: [
      {
        key: 'platformName',
        label: 'OS',
        selected: { label: 'GLOBAL', value: 'GLOBAL' },
        groups: ['app'],
        hidden: true,
        type: 'toggle-filter',
        options: [{ label: 'GLOBAL', value: 'GLOBAL' }]
      },
      {
        key: 'category',
        label: 'Category',
        selected: { label: 'All Categories', value: 'GLOBAL' },
        groups: ['app'],
        hidden: false,
        dependencies: [
          {
            group: 'app',
            key: 'countryCode'
          },
          {
            group: 'app',
            key: 'date'
          },
          {
            group: 'app',
            key: 'platformName'
          }
        ],
        options: [{ label: 'All Categories', value: 'GLOBAL' }]
      },
      {
        key: 'countryCode',
        label: 'Region',
        selected: { label: 'North America', value: 'NA' },
        hidden: false,
        dependencies: [
          {
            key: 'date'
          },
          {
            key: 'platformName'
          }
        ],
        options: [{ label: 'North America', value: 'NA' }]
      },
      {
        key: 'countryCode',
        label: 'Country',
        selected: { label: 'All Countries', value: 'GLOBAL' },
        hidden: false,
        dependencies: [
          {
            key: 'date'
          },
          {
            group: 'app',
            key: 'platformName'
          },
          {
            label: 'Region'
          }
        ],
        options: [{ label: 'All Countries', value: 'GLOBAL' }]
      },
      {
        key: 'date',
        label: 'Month',
        selected: null,
        hidden: false,
        dependencies: [
          {
            group: 'app',
            key: 'platformName'
          }
        ],
        options: []
      }
    ],
    gridHeaders: [
      {
        label: 'Rank',
        key: 'userRank',
        sort: '',
        sortDefault: false,
        sortColumnKey: '',
        hoverKey: '',
        template: null,
        widgetComponent: RankDataComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Device Brand Name',
        key: 'deviceBrandName',
        sort: '',
        groups: ['app'],
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: DataOnlyComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Platform',
        key: 'platformName',
        sort: '',
        groups: ['ctv'],
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: DataOnlyComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Estimated Reach',
        key: 'userSov',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        format: 'percent',
        template: null,
        widgetComponent: PercentDataComponent,
        hidden: false,
        lockIfFreemium: true,
        headerWidgetComponent: GridHeaderComponent
      }
    ],
    pageTitle:
      'Estimated Reach for IoT Devices, a Pixalate Rankings index:  for {type}',
    pageMeta:
      'IoT Estimated Reach measures estimated reach for {type} across {countryCode} for {date}.'
  },
  {
    key: 'device-market-share',
    heading: 'Market Share',
    title: 'Market Share',
    reportId: 'iotDeviceMarketShare',
    permissions: ['mrtSellerTrustIndex', 'freemium'],
    mainFilterItems: [
      {
        key: 'platformName',
        label: 'OS',
        selected: { label: 'GLOBAL', value: 'GLOBAL' },
        groups: ['app'],
        hidden: true,
        type: 'toggle-filter',
        options: [{ label: 'GLOBAL', value: 'GLOBAL' }]
      },
      {
        key: 'category',
        label: 'Category',
        selected: { label: 'All Categories', value: 'GLOBAL' },
        groups: ['app'],
        hidden: false,
        dependencies: [
          {
            group: 'app',
            key: 'countryCode'
          },
          {
            group: 'app',
            key: 'date'
          },
          {
            group: 'app',
            key: 'platformName'
          }
        ],
        options: [{ label: 'All Categories', value: 'GLOBAL' }]
      },
      {
        key: 'countryCode',
        label: 'Region',
        selected: { label: 'North America', value: 'NA' },
        hidden: false,
        dependencies: [
          {
            group: 'app',
            key: 'date'
          },
          {
            group: 'app',
            key: 'platformName'
          },
          {
            group: 'ctv',
            key: 'date'
          }
        ],
        options: [{ label: 'North America', value: 'NA' }]
      },
      {
        key: 'countryCode',
        label: 'Country',
        selected: { label: 'All Countries', value: 'GLOBAL' },
        hidden: false,
        dependencies: [
          {
            key: 'date'
          },
          {
            group: 'app',
            key: 'platformName'
          },
          {
            label: 'Region'
          }
        ],
        options: [{ label: 'All Countries', value: 'GLOBAL' }]
      },
      {
        key: 'date',
        label: 'Month',
        selected: null,
        hidden: false,
        dependencies: [
          {
            group: 'app',
            key: 'platformName'
          }
        ],
        options: []
      }
    ],
    gridHeaders: [
      {
        label: 'Rank',
        key: 'impressionRank',
        sort: '',
        sortDefault: false,
        sortColumnKey: '',
        hoverKey: '',
        template: null,
        widgetComponent: RankDataComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Device Brand Name',
        key: 'deviceBrandName',
        sort: '',
        groups: ['app'],
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: DataOnlyComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Platform',
        key: 'platformName',
        sort: '',
        groups: ['ctv'],
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: DataOnlyComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Estimated Market Reach',
        key: 'impressionSov',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        format: 'percent',
        template: null,
        widgetComponent: PercentDataComponent,
        hidden: false,
        lockIfFreemium: true,
        headerWidgetComponent: GridHeaderComponent
      }
    ],
    pageTitle:
      'Market Share for IoT Devices, a Pixalate Rankings index: for {type}',
    pageMeta:
      'IoT Market Share measures estimated market reach for {type} across {countryCode} for {date}.'
  },
  {
    key: 'high-risk-mfa',
    heading: 'Made For Advertising (MFA)',
    title: 'Made For Advertising',
    reportId: 'mfaIndex',
    permissions: ['freemium'],
    mainFilterItems: [
      {
        key: 'date',
        label: 'Month',
        selected: null,
        hidden: false,
        dependencies: [],
        options: []
      }
    ],
    notHideDataIfNA: true,
    gridHeaders: [
      {
        label: 'Rank',
        key: 'rank',
        sort: '',
        sortDefault: false,
        sortColumnKey: '',
        hoverKey: '',
        template: null,
        hidden: false,
        widgetComponent: RankDataComponent,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'App Name',
        key: 'appTitle',
        groups: ['app', 'ctv'],
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: AppNameComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Domain',
        key: 'adDomain',
        groups: ['domain'],
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: DomainNameComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Final Score',
        key: 'mfaFinalScore',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        tooltipText: {
          app: TOOLTIPS.mfaFinalScore,
          ctv: TOOLTIPS.mfaFinalScore,
          domain: TOOLTIPS.mfaFinalScore
        },
        headerWidgetComponent: GridHeaderWithTooltipComponent
      },
      {
        label: 'Ad Refresh Score',
        key: 'noOfAdsScore',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        tooltipText: {
          app: null,
          ctv: null,
          domain:
            'Based on the number of ad impressions presented to a user per minute.'
        },
        headerWidgetComponent: GridHeaderWithTooltipComponent
      },
      {
        label: 'Ad Density Score',
        key: 'noOfPlacementScore',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        tooltipText: {
          app: null,
          ctv: null,
          domain: 'Based on the number of ad placements found on a page.'
        },
        headerWidgetComponent: GridHeaderWithTooltipComponent
      },
      {
        label: 'Inorganic Traffic Score',
        key: 'socialPaidRateScore',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        tooltipText: {
          app: null,
          ctv: null,
          domain:
            'Based on the amount of traffic sourced via paid sources (ads) or social media sources.'
        },
        headerWidgetComponent: GridHeaderWithTooltipComponent
      }
    ],
    pageTitle: 'High Risk Made for Advertising Websites',
    pageMeta:
      "Explore Pixalate's comprehensive list of high-risk MFA sites, categorized using our groundbreaking, multi-platform MFA Risk Detection system designed to identify and evaluate 'Made for Advertising' (MFA) threats across web, mobile, and CTV platforms."
  }
];

export const SELLER_REPORT_CONFIG: ReportConfig[] = [
  {
    key: 'seller-trust-index',
    heading: 'Filtered by Seller',
    title: 'Filtered by Seller',
    reportId: 'sellerTrustIndex',
    permissions: ['mrtSellerTrustIndex', 'freemium'],
    mainFilterItems: [],
    notHideDataIfNA: true,
    gridHeaders: [
      {
        label: 'Region',
        key: 'parentRegion',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: SellerRegionComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Country',
        key: 'countryName',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: SellerCountryComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Rank',
        key: 'rank',
        sort: '',
        sortDefault: false,
        sortColumnKey: '',
        hoverKey: '',
        template: null,
        hidden: false,
        widgetComponent: RankDataComponent,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Final Score',
        key: 'finalScore',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      },
      {
        label: 'Market Share Score',
        key: 'marketShareScore',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderWithTooltipComponent,
        tooltipText: {
          app: TOOLTIPS.marketShareScore,
          ctv: TOOLTIPS.marketShareScore,
          domain: TOOLTIPS.marketShareScore
        }
      },
      {
        label: 'SPO Score',
        key: 'spoOptimizationScore',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderWithTooltipComponent,
        tooltipText: {
          app: TOOLTIPS.spoOptimizationScore,
          ctv: TOOLTIPS.spoOptimizationScore,
          domain: TOOLTIPS.spoOptimizationScore
        }
      },
      {
        label: 'SCO Verification Score',
        key: 'scoVerificationScore',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderWithTooltipComponent,
        tooltipText: {
          app: TOOLTIPS.scoVerificationScore,
          ctv: TOOLTIPS.scoVerificationScore,
          domain: TOOLTIPS.scoVerificationScore
        }
      },
      {
        label: 'Bundle Integrity',
        key: 'bundleIntegrityScore',
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderWithTooltipComponent,
        tooltipText: {
          app: null,
          ctv: `Represents the amount of traffic from the seller that uses standardized app store IDs in the bid stream.`,
          domain: null
        }
      },
      {
        label: 'GIVT Score',
        key: 'givtScore',
        groups: ['app', 'domain'],
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderWithTooltipComponent,
        tooltipText: {
          app: TOOLTIPS.givtScore,
          ctv: null,
          domain: TOOLTIPS.givtScore
        }
      },
      {
        label: 'SIVT Score',
        key: 'sivtScore',
        groups: ['app', 'domain'],
        sort: '',
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderWithTooltipComponent,
        tooltipText: {
          app: TOOLTIPS.sivtScore,
          ctv: null,
          domain: TOOLTIPS.sivtScore
        }
      },
      {
        label: 'IVT Score',
        key: 'ivtScore',
        sort: '',
        groups: ['ctv'],
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RankGradeComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderWithTooltipComponent,
        tooltipText: {
          app: null,
          ctv: `Reflects the levels of Invalid Traffic (IVT) on the impressions associated with the seller.`,
          domain: null
        }
      },
      {
        label: 'MFA Risk Score',
        key: 'mfaRiskScore',
        sort: '',
        groups: ['domain'],
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: RatingPillComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderWithTooltipComponent,
        tooltipText: {
          app: TOOLTIPS.mfaRiskScore,
          ctv: null,
          domain: TOOLTIPS.mfaRiskScore
        }
      },
      {
        label: 'Website Count',
        key: 'websiteCount',
        sort: '',
        groups: ['domain'],
        sortDefault: false,
        sortColumnKey: null,
        hoverKey: '',
        template: null,
        widgetComponent: BigNumberDataComponent,
        hidden: false,
        headerWidgetComponent: GridHeaderComponent
      }
    ],
    sortable: true
  }
];

export const REGION_COUNTRY_MAPPING = [
  {
    region: 'APAC',
    countries:
      'AS,AU,BD,BN,BT,CC,CN,CX,FJ,FM,GU,HK,ID,IN,IO,JP,KH,KI,KP,KR,LA,LK,MH,MM,MN,MO,MP,MV,MY,NF,NP,NR,NU,NZ,PG,PH,PK,PN,PW,SB,SG,TH,TK,TL,TO,TV,TW,UM,VN,VU,WS'
  },
  {
    region: 'EMEA',
    countries:
      'AD,AE,AF,AL,AM,AO,AQ,AT,AX,AZ,BA,BE,BF,BG,BH,BI,BJ,BL,BV,BW,BY,CD,CF,CG,CH,CI,CK,CM,CV,CW,CY,CZ,DE,DJ,DK,DZ,EE,EG,EH,ER,ES,ET,FI,FO,FR,GA,GB,GE,GF,GG,GH,GI,GL,GM,GN,GP,GQ,GR,GW,HM,HR,HU,IE,IL,IM,IQ,IR,IS,IT,JE,JO,KE,KG,KM,KW,KZ,LB,LI,LR,LS,LT,LU,LV,LY,MA,MC,MD,ME,MF,MG,MK,ML,MQ,MR,MT,MU,MW,MZ,NA,NAM,NC,NE,NG,NL,NO,OM,PF,PL,PM,PS,PT,QA,RE,RO,RS,RU,RW,SA,SC,SD,SE,SH,SI,SJ,SK,SL,SM,SN,SO,SS,ST,SX,SY,SZ,TD,TF,TG,TJ,TM,TN,TR,TZ,UA,UG,UZ,VA,WF,XK,YE,YT,ZA,ZM,ZW'
  },
  {
    region: 'LATAM',
    countries:
      'AG,AI,AR,AW,BB,BM,BO,BQ,BR,BS,BZ,CL,CO,CR,CU,DM,DO,EC,FK,GD,GS,GT,GY,HN,HT,JM,KN,KY,LC,MS,MX,NI,PA,PE,PR,PY,SR,SV,TC,TT,UY,VC,VE,VG,VI'
  },
  {
    region: 'NA',
    countries: 'CA,US'
  },
  {
    region: 'US'
  },
  {
    region: 'NON-US'
  }
];

export const DEFAULT_COUNTRY_PER_REGION_MAPPING = {
  NA: 'US',
  APAC: 'CN',
  EMEA: 'DE',
  LATAM: 'BR'
};
